import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  TableSortLabel,
  Checkbox,
  Chip,
  Popover,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandIcon } from '@mui/icons-material';
import CompactPagination from './CompactPagination';
import EditTextDialog from './EditTextDialog';
import {
  ContentCopy as DuplicateIcon,
  Undo as RevertIcon,
  Transform as ConvertIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useAppStore } from '../state/store';
import { Task, Takeaway, UserField, SalesforceUser } from '../types';
import { userService } from '../services/userService';

  // Unified item type for the grid
interface UnifiedItem {
  id: string;
  rowType: 'Task' | 'Takeaway';
  title: string;
  description: string;
  requester: UserField | null;
  owner: UserField | null; // "Assign To" column
  ownerCandidates: string; // "Candidates (confidence)"
  due: string | null;
  type: string;
  priority: 'high' | 'normal' | null;
  tags: string[];
  citation: string; // Evidence quotes
  confidence: number;
  isProcessed?: boolean; // Task is locked after Salesforce send
  sentToSalesforceAt?: string;
  sentToSalesforceBy?: string;
  
  // Task-only fields
  origin?: 'native' | 'copy' | 'converted' | 'manual';
  originalId?: string; // For converted tasks, links to original takeaway RowKey
  isNew?: boolean; // Flag for duplicated/converted tasks
}

interface Column {
  id: keyof UnifiedItem | 'actions';
  label: string;
  width: number;
  sortable: boolean;
  editable: boolean;
  taskOnly: boolean; // True if this field should be grayed out for takeaways
  type: 'text' | 'select' | 'date' | 'tags' | 'readonly' | 'actions';
  options?: string[];
}

// Column order as requested
const allColumns: Column[] = [
  { id: 'rowType', label: 'Row Type', width: 100, sortable: true, editable: false, taskOnly: false, type: 'readonly' }, // Task or Takeaway
  { id: 'title', label: 'Title', width: 224, sortable: true, editable: true, taskOnly: false, type: 'text' }, // 20% less than 280
  { id: 'description', label: 'Description', width: 300, sortable: true, editable: true, taskOnly: false, type: 'text' },
  { id: 'requester', label: 'Requester', width: 130, sortable: true, editable: true, taskOnly: true, type: 'select', options: [] },
  { id: 'owner', label: 'Assign To', width: 140, sortable: true, editable: true, taskOnly: true, type: 'select', options: [] },
  { id: 'ownerCandidates', label: 'Candidates (confidence)', width: 200, sortable: true, editable: false, taskOnly: false, type: 'readonly' },
  { id: 'due', label: 'Due Date', width: 140, sortable: true, editable: true, taskOnly: true, type: 'date' },
  { id: 'type', label: 'Type', width: 180, sortable: true, editable: true, taskOnly: false, type: 'select', options: [] }, // ItemType from backend
  { id: 'priority', label: 'Priority', width: 110, sortable: true, editable: false, taskOnly: true, type: 'readonly', options: [] },
  { id: 'tags', label: 'Tags', width: 120, sortable: false, editable: true, taskOnly: false, type: 'text' },
  { id: 'citation', label: 'Citation', width: 300, sortable: false, editable: false, taskOnly: false, type: 'readonly' },
  { id: 'confidence', label: 'Confidence', width: 100, sortable: true, editable: false, taskOnly: false, type: 'readonly' },
  { id: 'actions', label: 'Actions', width: 120, sortable: false, editable: false, taskOnly: false, type: 'actions' },
];

// Filter out hidden columns (priority and tags)
const columns = allColumns.filter(col => col.id !== 'priority' && col.id !== 'tags');

type SortOrder = 'asc' | 'desc';

interface ItemsGridProps {
  filterType: 'All' | 'Tasks' | 'Takeaways';
  selectedItemIds: Set<string>;
  onSelectedItemIdsChange: (selectedIds: Set<string>) => void;
  onDeleteItem: (item: UnifiedItem) => void;
}

const ItemsGrid: React.FC<ItemsGridProps> = ({ 
  filterType, 
  selectedItemIds, 
  onSelectedItemIdsChange,
  onDeleteItem
}) => {
  const { tasks, takeaways, updateTask, updateTakeaway, duplicateTask, deleteTask, revertTask, convertTakeawayToTask } = useAppStore();
  
  const [editingCell, setEditingCell] = useState<{ id: string; field: keyof UnifiedItem } | null>(null);
  const [editingValue, setEditingValue] = useState<any>(null); // Local state for editing
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogField, setDialogField] = useState<{ item: UnifiedItem; field: keyof UnifiedItem; label: string } | null>(null);
  const [sortField, setSortField] = useState<keyof UnifiedItem | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Citation popover state
  const [citationAnchor, setCitationAnchor] = useState<{ element: HTMLElement; text: string } | null>(null);
  
  // Configuration data
  const [owners, setOwners] = useState<SalesforceUser[]>([]);
  const [priorities, setPriorities] = useState<string[]>([]);
  const [types, setTypes] = useState<Array<{value: string, label: string}>>([]);

  // Load configuration data
  React.useEffect(() => {
    const loadConfig = async () => {
      try {
        const [usersData, prioritiesRes, typesRes] = await Promise.all([
          userService.fetchUsers(), // Fetch users from Users table via API
          fetch('/config/priorities.json'),
          fetch('/config/types.json'),
        ]);
        
        const prioritiesData = await prioritiesRes.json();
        const typesData = await typesRes.json();
        
        setOwners(usersData);
        setPriorities(Array.isArray(prioritiesData) ? prioritiesData : []);
        setTypes(Array.isArray(typesData) ? typesData : []);
      } catch (error) {
        console.error('Failed to load configuration:', error);
      }
    };
    
    loadConfig();
  }, []);

  // Validation logic for tasks (required for Salesforce)
  const isTaskCompleteForSalesforce = (task: UnifiedItem): boolean => {
    if (task.rowType !== 'Task') return false;
    
    const validTypeValues = types.map(t => t.value);
    
    // Owner and Requester must have resolved user_id (not empty)
    const hasValidOwner = task.owner && task.owner.userId && task.owner.userId.trim() !== '';
    const hasValidRequester = task.requester && task.requester.userId && task.requester.userId.trim() !== '';
    const hasValidType = task.type?.trim() && validTypeValues.includes(task.type.trim());
    const hasTags = task.tags && task.tags.length > 0;
    
    return !!(
      task.title?.trim() &&
      task.description?.trim() &&
      hasValidOwner &&
      hasValidRequester &&
      task.due?.trim() &&
      hasValidType &&
      hasTags
    );
  };

  // Get missing fields for a task
  const getMissingFields = (task: UnifiedItem): string[] => {
    const missing: string[] = [];
    const validTypeValues = types.map(t => t.value);
    
    if (!task.title?.trim()) missing.push('Title');
    if (!task.description?.trim()) missing.push('Description');
    if (!task.requester || !task.requester.userId || task.requester.userId.trim() === '') {
      missing.push('Requester (unresolved)');
    }
    if (!task.owner || !task.owner.userId || task.owner.userId.trim() === '') {
      missing.push('Assign To (unresolved)');
    }
    if (!task.due?.trim()) missing.push('Due Date');
    if (!task.type?.trim()) {
      missing.push('Type');
    } else if (!validTypeValues.includes(task.type.trim())) {
      missing.push('Type (not in system)');
    }
    if (!task.tags || task.tags.length === 0) missing.push('Tags');
    return missing;
  };

  // Helper function to safely format citation from evidence array
  const formatCitation = (evidence: any[] | undefined): string => {
    if (!evidence || !Array.isArray(evidence)) return '';
    return evidence
      .filter(e => e && e.quote)
      .map(e => {
        const quote = e.quote;
        const hasSpeaker = e.speaker && e.speaker.trim() !== '';
        const hasTime = e.elapsed_time && e.elapsed_time.trim() !== '';
        
        const speaker = hasSpeaker ? e.speaker : 'Unknown';
        
        if (hasTime) {
          return `${quote} (${speaker} ${e.elapsed_time})`;
        } else {
          return `${quote} (${speaker})`;
        }
      })
      .join(' | ');
  };

  // Convert tasks and takeaways to unified items
  const unifiedItems: UnifiedItem[] = useMemo(() => {
    const taskItems: UnifiedItem[] = tasks
      .filter(task => !task.isDeleted) // Hide deleted tasks
      .map(task => ({
      id: task.id,
      rowType: 'Task' as const,
      title: task.title,
      description: task.description,
      requester: task.requester,
      owner: task.owner,
      ownerCandidates: task.ownerCandidates,
      due: task.due,
      type: task.type,
      priority: task.priority,
      tags: task.tags,
      citation: formatCitation(task.evidence),
      confidence: task.confidence,
      origin: task.origin,
      originalId: task.originalId,
      isNew: task.isNew, // Pass through isNew flag
      isProcessed: task.isProcessed, // Pass through Salesforce lock status
      sentToSalesforceAt: task.sentToSalesforceAt,
      sentToSalesforceBy: task.sentToSalesforceBy,
    }));

    const takeawayItems: UnifiedItem[] = takeaways
      .filter(takeaway => !takeaway.isConverted && !takeaway.isDeleted) // Hide converted and deleted takeaways
      .map(takeaway => ({
        id: takeaway.id,
        rowType: 'Takeaway' as const,
        title: takeaway.name,
        description: takeaway.description,
        requester: null,
        owner: null,
        ownerCandidates: '',
        due: null,
        type: takeaway.type,
        priority: null,
        tags: takeaway.tags || [],
        citation: formatCitation(takeaway.evidence),
        confidence: takeaway.confidence,
      }));

    return [...taskItems, ...takeawayItems];
  }, [tasks, takeaways]);

  // Filter based on type
  const filteredItems = useMemo(() => {
    if (filterType === 'All') return unifiedItems;
    return unifiedItems.filter(item => item.rowType === filterType.slice(0, -1)); // Remove 's' from "Tasks"/"Takeaways"
  }, [unifiedItems, filterType]);

  // Sorting logic
  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      // If no sort field is selected, default to Tasks first, then Takeaways
      if (!sortField) {
        const rowTypeComparison = a.rowType === 'Task' && b.rowType === 'Takeaway' ? -1 
          : a.rowType === 'Takeaway' && b.rowType === 'Task' ? 1 
          : 0;
        return rowTypeComparison;
      }
      
      // Sort by the selected field
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      // Special handling for due dates
      if (sortField === 'due') {
        const aDate = aValue ? new Date(aValue as string) : null;
        const bDate = bValue ? new Date(bValue as string) : null;
        
        if (!aDate && !bDate) return 0;
        if (!aDate) return sortOrder === 'asc' ? 1 : -1;
        if (!bDate) return sortOrder === 'asc' ? -1 : 1;
        
        const comparison = aDate.getTime() - bDate.getTime();
        return sortOrder === 'desc' ? -comparison : comparison;
      }
      
      // Regular sorting
      const aStr = aValue || '';
      const bStr = bValue || '';
      
      let comparison = 0;
      if (aStr < bStr) comparison = -1;
      if (aStr > bStr) comparison = 1;
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [filteredItems, sortField, sortOrder]);

  // Pagination
  const paginatedItems = sortedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (field: keyof UnifiedItem) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const handleSelectItem = (id: string, rowType: string) => {
    if (rowType !== 'Task') return; // Only tasks can be selected
    
    // Find the item to check if it's complete
    const item = sortedItems.find(i => i.id === id);
    if (!item || !isTaskCompleteForSalesforce(item)) {
      // Can't select incomplete tasks - show warning
      return;
    }
    
    const newSelected = new Set(selectedItemIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    onSelectedItemIdsChange(newSelected);
  };

  const handleCellClick = (itemId: string, field: keyof UnifiedItem, item: UnifiedItem) => {
    const column = columns.find(c => c.id === field);
    if (!column?.editable) return;
    
    // Don't allow editing processed tasks
    if (item.rowType === 'Task' && item.isProcessed) return;
    
    // Don't allow editing task-only fields for takeaways
    if (column.taskOnly && item.rowType === 'Takeaway') return;
    
    // For owner/requester fields, extract display name for the dropdown
    let initialValue = item[field];
    if (field === 'owner' || field === 'requester') {
      const userField = item[field] as UserField | null;
      if (userField) {
        if (userField.userId && userField.userId.trim() !== '') {
          // Resolved: find the user and use their name
          const user = owners.find(u => u.id === userField.userId);
          initialValue = user ? user.name : userField.name;
        } else {
          // Unresolved: use originalValue
          initialValue = userField.originalValue;
        }
      } else {
        initialValue = '';
      }
    }
    
    setEditingCell({ id: itemId, field });
    setEditingValue(initialValue);
  };

  const handleCellDoubleClick = (item: UnifiedItem, field: keyof UnifiedItem) => {
    const column = columns.find(c => c.id === field);
    if (!column?.editable || column.type !== 'text') return;
    
    // Don't allow editing processed tasks
    if (item.rowType === 'Task' && item.isProcessed) return;
    
    // Don't allow editing task-only fields for takeaways
    if (column.taskOnly && item.rowType === 'Takeaway') return;
    
    // Open dialog for text fields
    setDialogField({ item, field, label: column.label });
    setDialogOpen(true);
  };

  const handleDialogSave = (value: string) => {
    if (dialogField) {
      const { item, field } = dialogField;
      
      // Special handling for tags - convert comma-separated string to array
      if (field === 'tags') {
        // If empty or whitespace-only, save as empty array
        if (!value || value.trim() === '') {
          handleCellSave(item.id, field, [], item);
        } else {
          const tagsArray = value
            .split(',')
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0);
          handleCellSave(item.id, field, tagsArray, item);
        }
      } else {
        handleCellSave(item.id, field, value, item);
      }
    }
    setDialogOpen(false);
    setDialogField(null);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setDialogField(null);
  };

  const handleCellSave = (itemId: string, field: keyof UnifiedItem, value: any, item: UnifiedItem) => {
    if (item.rowType === 'Task') {
      // Special handling for owner/requester fields - convert name string to UserField
      if (field === 'owner' || field === 'requester') {
        const selectedName = value as string;
        let userField: UserField | null = null;
        
        if (selectedName && selectedName.trim() !== '') {
          // Find the user by name
          const user = owners.find(u => u.name === selectedName);
          if (user) {
            // Create resolved UserField
            userField = {
              userId: user.id,
              email: user.email,
              name: user.name,
              originalValue: selectedName
            };
          } else {
            // Create unresolved UserField (user typed a custom name)
            userField = {
              userId: '',
              email: '',
              name: '',
              originalValue: selectedName
            };
          }
        }
        
        updateTask(itemId, { [field]: userField } as Partial<Task>);
      } else {
      updateTask(itemId, { [field]: value } as Partial<Task>);
      }
    } else {
      // Only update non-task-specific fields for takeaways
      if (field === 'title') {
        updateTakeaway(itemId, { name: value });
      } else if (field === 'description' || field === 'type' || field === 'tags') {
        updateTakeaway(itemId, { [field]: value } as Partial<Takeaway>);
      }
    }
    
    setEditingCell(null);
    setEditingValue(null);
  };
  
  const handleCellBlur = (itemId: string, field: keyof UnifiedItem, item: UnifiedItem) => {
    if (editingValue !== undefined && editingValue !== null) {
      handleCellSave(itemId, field, editingValue, item);
    } else {
      setEditingCell(null);
      setEditingValue(null);
    }
  };

  const handleDuplicate = (item: UnifiedItem) => {
    if (item.rowType === 'Task') {
      duplicateTask(item.id);
    }
    // Takeaways don't have duplicate functionality
  };

  const handleRevert = (item: UnifiedItem) => {
    if (item.rowType === 'Task') {
      // Check if it's a duplicated task (new, not yet saved)
      const task = tasks.find(t => t.id === item.id);
      if (task?.isNew && task.origin === 'copy') {
        // Delete duplicated tasks that haven't been saved yet
        deleteTask(item.id);
      } else if (task?.isNew && task.origin === 'converted') {
        // Revert converted tasks back to takeaways
        revertTask(item.id);
      }
    }
  };

  const handleConvert = (item: UnifiedItem) => {
    if (item.rowType === 'Takeaway') {
      convertTakeawayToTask(item.id);
    }
  };

  const renderCell = (item: UnifiedItem, column: Column) => {
    const isEditing = editingCell?.id === item.id && editingCell?.field === column.id;
    const value = item[column.id as keyof UnifiedItem];
    const isTaskOnlyField = column.taskOnly && item.rowType === 'Takeaway';
    
    // Action column
    if (column.type === 'actions') {
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {item.rowType === 'Task' && !item.isProcessed && (
            <>
              <Tooltip title="Duplicate Task">
                <IconButton size="small" onClick={() => handleDuplicate(item)}>
                  <DuplicateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Show revert button for converted tasks (revert to takeaway) or duplicated tasks (delete) */}
              {(item.origin === 'converted' || item.origin === 'copy') && item.isNew && (
                <Tooltip title={tasks.find(t => t.id === item.id)?.isNew ? "Remove Duplicate" : "Revert to Takeaway"}>
                  <IconButton size="small" onClick={() => handleRevert(item)}>
                    <RevertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Delete Task">
                <IconButton 
                  size="small" 
                  onClick={() => onDeleteItem(item)}
                  sx={{ 
                    color: 'error.main',
                    '&:hover': { backgroundColor: 'error.light', opacity: 0.1 }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {item.rowType === 'Takeaway' && (
            <>
              <Tooltip title="Convert to Task">
                <IconButton size="small" onClick={() => handleConvert(item)}>
                  <ConvertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Takeaway">
                <IconButton 
                  size="small" 
                  onClick={() => onDeleteItem(item)}
                  sx={{ 
                    color: 'error.main',
                    '&:hover': { backgroundColor: 'error.light', opacity: 0.1 }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      );
    }
    
    // Readonly columns
    if (!column.editable || isTaskOnlyField) {
      // Special handling for citation field - truncate and show popover
      if (column.id === 'citation') {
        const citationText = value as string || '';
        // Show expand icon if text could span more than 5 lines (more conservative estimate)
        const isTruncated = citationText.length > 120;
        
        return (
          <Box 
            sx={{ 
              opacity: isTaskOnlyField ? 0.3 : 1, 
              fontStyle: isTaskOnlyField ? 'italic' : 'normal',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                fontSize: '0.875rem',
                lineHeight: 1.4
              }}
            >
              {citationText || '-'}
            </Box>
            {isTruncated && citationText && (
              <IconButton
                size="small"
                onClick={(e) => setCitationAnchor({ element: e.currentTarget, text: citationText })}
                sx={{ 
                  padding: '2px',
                  minWidth: 'auto',
                  color: 'primary.main'
                }}
              >
                <ExpandIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      }
      
      // Special handling for ownerCandidates - truncate similarly
      if (column.id === 'ownerCandidates') {
        const candidatesText = value as string || '';
        // Show expand icon if text could span more than 2 lines (more conservative estimate)
        const isTruncated = candidatesText.length > 50;
        
        return (
          <Box 
            sx={{ 
              opacity: isTaskOnlyField ? 0.3 : 1, 
              fontStyle: isTaskOnlyField ? 'italic' : 'normal',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                fontSize: '0.875rem',
                lineHeight: 1.4
              }}
            >
              {candidatesText || '-'}
            </Box>
            {isTruncated && candidatesText && (
              <IconButton
                size="small"
                onClick={(e) => setCitationAnchor({ element: e.currentTarget, text: candidatesText })}
                sx={{ 
                  padding: '2px',
                  minWidth: 'auto',
                  color: 'primary.main'
                }}
              >
                <ExpandIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      }
      
      const displayValue = column.id === 'confidence' 
        ? `${Math.round((typeof value === 'number' ? value : 0) * 100)}%`
        : column.id === 'rowType'
        ? value === 'Task'
          ? <Chip label="Task" size="small" color="primary" />
          : <Chip 
              label="Insight" 
              size="small" 
              sx={{
                backgroundColor: '#ffd700',
                color: '#000000',
                fontWeight: 500
              }}
            />
        : value as string;
        
      return (
        <Box sx={{ opacity: isTaskOnlyField ? 0.3 : 1, fontStyle: isTaskOnlyField ? 'italic' : 'normal' }}>
          {displayValue || '-'}
        </Box>
      );
    }
    
    // Editable cells - skip inline editing for text fields (use dialog instead)
    if (isEditing && column.type !== 'text') {
      switch (column.type) {
        case 'select':
          const options = column.id === 'owner' || column.id === 'requester' 
            ? owners.map(o => o.name)
            : column.id === 'priority'
            ? priorities
            : column.id === 'type'
            ? types.map(t => t.label)
            : [];
            
          return (
            <FormControl fullWidth size="small">
              <Select
                value={editingValue || ''}
                onChange={(e) => {
                  setEditingValue(e.target.value);
                  handleCellSave(item.id, column.id as keyof UnifiedItem, e.target.value, item);
                }}
                autoFocus
                onBlur={() => handleCellBlur(item.id, column.id as keyof UnifiedItem, item)}
              >
                <MenuItem value="">None</MenuItem>
                {options.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
          
        case 'date':
          return (
            <DatePicker
              value={editingValue ? dayjs(editingValue as string) : null}
              onChange={(newValue: Dayjs | null) => {
                const formatted = newValue?.format('YYYY-MM-DD') || null;
                setEditingValue(formatted);
                handleCellSave(item.id, column.id as keyof UnifiedItem, formatted, item);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  autoFocus: true,
                },
              }}
              open={true}
              onClose={() => {
                setEditingCell(null);
                setEditingValue(null);
              }}
            />
          );
          
      }
    }
    
    // For text fields, if somehow in editing mode, close it and open dialog instead
    if (isEditing && column.type === 'text') {
      setEditingCell(null);
      setEditingValue(null);
    }
    
    // Display mode for priority - show as colored chip
    if (column.id === 'priority') {
      const priorityValue = value as 'high' | 'normal' | null;
      const priorityColors = {
        high: { bgcolor: '#d32f2f', color: 'white' },
        normal: { bgcolor: '#2e7d32', color: 'white' }
      };
      
      return (
        <Box sx={{ opacity: isTaskOnlyField ? 0.3 : 1 }}>
          {priorityValue ? (
            <Chip 
              label={priorityValue.charAt(0).toUpperCase() + priorityValue.slice(1)} 
              size="small" 
              sx={{ 
                ...priorityColors[priorityValue],
                fontWeight: 500
              }} 
            />
          ) : '-'}
        </Box>
      );
    }
    
    // Display mode for tags - show as chips with dialog editing
    if (column.id === 'tags') {
      const tags = value as string[] || [];
      return (
        <Box 
          onDoubleClick={(e) => {
            e.stopPropagation();
            handleCellDoubleClick(item, column.id as keyof UnifiedItem);
          }}
          sx={{ 
            opacity: isTaskOnlyField ? 0.3 : 1,
            cursor: column.editable && !isTaskOnlyField ? 'pointer' : 'default',
            '&:hover': column.editable && !isTaskOnlyField ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' } : {}
          }}
          title={column.editable && !isTaskOnlyField ? "Double-click to edit" : undefined}
        >
          {tags.length > 0 ? (
            tags.map((tag, idx) => (
            <Chip key={idx} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
            ))
          ) : '-'}
        </Box>
      );
    }
    
    if (column.type === 'date') {
      return (
        <Box onClick={() => handleCellClick(item.id, column.id as keyof UnifiedItem, item)}>
          {value ? dayjs(value as string).format('MMM D, YYYY') : '-'}
        </Box>
      );
    }
    
    // Special handling for owner/requester UserField objects
    if (column.id === 'owner' || column.id === 'requester') {
      const userField = value as UserField | null;
      let displayValue = '-';
      let isResolved = false;
      
      if (userField) {
        if (userField.userId && userField.userId.trim() !== '') {
          // Resolved: show "Name (email)" format
          const user = owners.find(u => u.id === userField.userId);
          displayValue = user ? userService.formatUserDisplay(user) : userField.name;
          isResolved = true;
        } else {
          // Unresolved: show originalValue
          displayValue = userField.originalValue || '-';
          isResolved = false;
        }
      }
      
      return (
        <Box 
          onClick={() => handleCellClick(item.id, column.id as keyof UnifiedItem, item)}
          sx={{ 
            color: isResolved ? 'inherit' : '#d32f2f',
            fontStyle: isResolved ? 'normal' : 'italic'
          }}
        >
          {displayValue}
        </Box>
      );
    }
    
    // Text fields - show hint about double-click, with truncation
    if (column.type === 'text') {
      const textValue = value as string || '';
      // Show expand icon if text could span more than 5 lines (more conservative estimate)
      const isTruncated = textValue.length > 120;
      
      return (
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5
          }}
        >
        <Box 
          onDoubleClick={(e) => {
            e.stopPropagation();
            handleCellDoubleClick(item, column.id as keyof UnifiedItem);
          }}
          sx={{ 
              flex: 1,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 5,
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis',
              fontSize: '0.875rem',
              lineHeight: 1.4,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
            title="Double-click to edit"
        >
            {textValue || '-'}
          </Box>
          {isTruncated && textValue && (
            <IconButton
              size="small"
              onClick={(e) => setCitationAnchor({ element: e.currentTarget, text: textValue })}
              sx={{ 
                padding: '2px',
                minWidth: 'auto',
                color: 'primary.main'
              }}
            >
              <ExpandIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );
    }
    
    return (
      <Box onClick={() => handleCellClick(item.id, column.id as keyof UnifiedItem, item)}>
        {value as string || '-'}
      </Box>
    );
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Citation Popover */}
      <Popover
        open={Boolean(citationAnchor)}
        anchorEl={citationAnchor?.element}
        onClose={() => setCitationAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 600, maxHeight: 400, overflow: 'auto' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {citationAnchor?.text || ''}
          </Typography>
        </Box>
      </Popover>
      
      {/* Edit Text Dialog */}
      <EditTextDialog
        open={dialogOpen}
        title={`Edit ${dialogField?.label || 'Field'}`}
        value={
          dialogField 
            ? dialogField.field === 'tags' 
              ? (dialogField.item[dialogField.field] as string[] || []).join(', ')
              : (dialogField.item[dialogField.field] as string || '')
            : ''
        }
        multiline={dialogField?.field === 'description'}
        onSave={handleDialogSave}
        onCancel={handleDialogCancel}
        helperText={dialogField?.field === 'tags' ? 'Enter tags separated by commas' : undefined}
      />
      
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 50, backgroundColor: '#f5f5f5' }}>
                {/* Checkbox column for row selection - no header checkbox */}
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ 
                    width: column.width, 
                    minWidth: column.width,
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold'
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortField === column.id}
                      direction={sortField === column.id ? sortOrder : 'asc'}
                      onClick={() => handleSort(column.id as keyof UnifiedItem)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedItems.map((item) => (
              <TableRow 
                key={item.id}
                hover
                sx={{
                  '&:hover': { backgroundColor: '#fafafa' }
                }}
              >
                <TableCell padding="checkbox">
                  {item.rowType === 'Task' && (
                    item.isProcessed ? (
                      // Show green checkmark for processed tasks
                      <Tooltip title={`Task sent to Salesforce on ${item.sentToSalesforceAt ? new Date(item.sentToSalesforceAt).toLocaleString() : 'unknown date'}`}>
                        <span>
                          <IconButton size="small" disabled sx={{ padding: '4px' }}>
                            <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : isTaskCompleteForSalesforce(item) ? (
                      <Checkbox
                        checked={selectedItemIds.has(item.id)}
                        onChange={() => handleSelectItem(item.id, item.rowType)}
                        size="small"
                      />
                    ) : (
                      <Tooltip 
                        title={`Missing required fields: ${getMissingFields(item).join(', ')}`}
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{ 
                            color: 'warning.main',
                            padding: '4px'
                          }}
                        >
                          <WarningIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  )}
                </TableCell>
                {columns.map((column) => (
                  <TableCell 
                    key={column.id}
                    sx={{ 
                      maxWidth: column.width,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: column.editable && !(column.taskOnly && item.rowType === 'Takeaway') && !(item.rowType === 'Task' && item.isProcessed) ? 'pointer' : 'default',
                      opacity: (item.rowType === 'Task' && item.isProcessed) ? 0.6 : 1
                    }}
                  >
                    {renderCell(item, column)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ p: 1, borderTop: '1px solid #e0e0e0' }}>
        <CompactPagination
          count={sortedItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

export default ItemsGrid;

